import { useEffect, useState } from 'react';
import {
  NudosFileInput,
  NudosLink,
  NudosPhoneInput,
  NudosTextInput,
  NudosUploadDownloadButton
} from '../NudosComponents';
import NudosCountrySelect from '../NudosComponents/NudosCountrySelect/NudosCountrySelect';
import UnsuccessfulSearch from '../unsuccessfulSearch';
import { DTOgetOrderDetailedData, IbillingData, IPaymentData, ORDER_STATUS } from '../../types/orders';
import { getTaxDocumentText } from '../../utils/formValidations';
import { ICountryDetail } from '../../types/countries';
import { BlackCheck } from '../../views/countriesList/components';
import { CURRENCIES } from '../../types/bussines';
import { BillingFileComponent } from './Components';
import { IlogisticServiceDetails } from '../../types/services';
import PurchaseOrderSection from '../PurchaseOrderSection/PurchaseOrderSection';
import SummaryBanner from 'components/SummaryBanner/SummaryBanner';
import {
  SERVICE_SUMMARY_VARIANT_TEXT,
  SERVICE_SUMMARY_VARIANTS,
  SummaryVariant
} from 'components/SummaryBanner/SummaryBanner.types';
import { LIST_OF_QUOTE_STATUSES } from 'types/services';
import { getLogisticServiceSummaryUrl } from 'services/services';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import useOnlyDevelopers from 'state/setOnlyDevelopers';
import { getSpecialBusinessData } from '../../utils/productDefinitions';
import './BillingDataTab.scss';

const BillingDataTab = ({
  logisticServiceData,
  orderData,
  billingData,
  orderPaymentData,
  billingCountry,
  updateWithoutLoader,
  includePurchaseOrderSection = false
}: {
  logisticServiceData?: IlogisticServiceDetails;
  orderData?: DTOgetOrderDetailedData;
  billingData?: IbillingData;
  orderPaymentData?: IPaymentData;
  billingCountry?: ICountryDetail;
  updateWithoutLoader?: () => void;
  includePurchaseOrderSection?: boolean;
}) => {
  const [serviceSummaryVariant, setServiceSummaryVariant] = useState<SummaryVariant>(SERVICE_SUMMARY_VARIANTS.TO_QUOTE);
  const [summaryUrl, setSummaryUrl] = useState<string | undefined>(undefined);
  const [isLoadingSummaryUrl, setIsLoadingSummaryUrl] = useState<boolean>(false);

  const [orderSummaryVariant, setOrderSummaryVariant] = useState<SummaryVariant>(
    SERVICE_SUMMARY_VARIANTS.ORDER_SUMMARY
  );

  const { onlyDevelopers } = useOnlyDevelopers();
  const {
    businessName,
    document,
    taxSituationDocUrl,
    documentCountry,
    taxSituation,
    taxUse,
    specialBusinessNameTypeId,
    additionalBillingData
  } = billingData || {};
  const { city, zipCode, state, address, additionalReferences, email, phone, phoneCountry, fantasyName, colony } =
    billingData || {};
  const stateRegistration = additionalBillingData?.find(billingData => billingData.additionalBillingDataTypeId === 1);
  const showBillingFileComponent = !!logisticServiceData;
  const noBillingDataText =
    'Este servicio logístico no tiene información de facturación, de momento solo se tiene para aquellos con cotización automatizada';
  const noDataText = 'Sin datos';
  const documentCountryIsMexico = documentCountry?.code === 'mx';
  const documentCountryIsUruguay = documentCountry?.code === 'uy';
  const documentCountryIsBrasil = documentCountry?.code === 'br';
  const addressInThirdLine = ['mx', 'uy'].includes(documentCountry?.code || '');

  const totalAmount = Number(orderPaymentData?.total ?? logisticServiceData?.total ?? 0);
  const totalAmountUSD = Number(orderPaymentData?.totalUsd ?? logisticServiceData?.totalUsd ?? 0);

  const currency = orderPaymentData?.currency ?? logisticServiceData?.currency ?? CURRENCIES.USD;

  const summaryTitle = orderData
    ? SERVICE_SUMMARY_VARIANT_TEXT.orderSummary
    : SERVICE_SUMMARY_VARIANT_TEXT.serviceSummary;

  const determineServiceSummaryVariant = () => {
    const quotesLog = logisticServiceData?.quotesLog;
    const alternativeToPendingPayment = summaryUrl && !quotesLog?.status;
    if (!summaryUrl) return setServiceSummaryVariant(SERVICE_SUMMARY_VARIANTS.TO_QUOTE);
    if (quotesLog?.status === LIST_OF_QUOTE_STATUSES.APPROVED || !logisticServiceData?.manualQuote)
      return setServiceSummaryVariant(SERVICE_SUMMARY_VARIANTS.APPROVED);
    if (quotesLog?.status === LIST_OF_QUOTE_STATUSES.PENDING || alternativeToPendingPayment)
      return setServiceSummaryVariant(SERVICE_SUMMARY_VARIANTS.PENDING);
    if (quotesLog?.status === LIST_OF_QUOTE_STATUSES.REJECTED)
      return setServiceSummaryVariant(SERVICE_SUMMARY_VARIANTS.REJECTED);
  };

  /**
   * Obtiene la URL del resumen del servicio logístico.
   * Si soloDevelopers está activo, obtiene la URL desde la API.
   * En caso contrario, usa la URL del documento de cotización si está disponible.
   */
  const getSummaryUrl = async () => {
    if (!logisticServiceData?.id) return;

    setIsLoadingSummaryUrl(true);
    try {
      const url = onlyDevelopers
        ? (await getLogisticServiceSummaryUrl(logisticServiceData.id)).url
        : logisticServiceData.quoteDocumentUrl ?? undefined;

      setSummaryUrl(url);
    } catch (error) {
      displayErrorNotification({
        customJSXMessage: <>Error al obtener la Url del resumen del servicio logístico</>
      });
      setSummaryUrl(undefined);
    } finally {
      setIsLoadingSummaryUrl(false);
    }
  };

  useEffect(() => {
    getSummaryUrl();
  }, [logisticServiceData]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Determina la variante del banner de resumen para una orden
   * basándose en el estado de la orden
   */
  const determineOrderSummaryVariant = () => {
    if (!orderData) return;

    const { statusName } = orderData;

    const variant =
      statusName === ORDER_STATUS.CANCELLED
        ? SERVICE_SUMMARY_VARIANTS.CANCELLED
        : SERVICE_SUMMARY_VARIANTS.ORDER_SUMMARY;

    setOrderSummaryVariant(variant);
  };

  useEffect(() => {
    determineServiceSummaryVariant();
  }, [summaryUrl, logisticServiceData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    determineOrderSummaryVariant();
  }, [orderData]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!billingData) return <UnsuccessfulSearch hideIcon customText={noBillingDataText} />;

  return (
    <div className="billingDataTab">
      {(logisticServiceData || orderData) && (
        <>
          {isLoadingSummaryUrl ? (
            <div className="serviceSummarySkeletonLoader">
              <div className="skeletonHeader">
                <div className="skeletonTitle animationLoader" />
                <div className="skeletonButton animationLoader" />
              </div>
              <div className="skeletonBanner animationLoader" />
            </div>
          ) : (
            <SummaryBanner
              summaryTitle={summaryTitle}
              variant={orderData ? orderSummaryVariant : serviceSummaryVariant}
              id={orderData ? Number(orderData?.orderId) : logisticServiceData?.id || undefined}
              quoteLink={summaryUrl || undefined}
              LSData={logisticServiceData}
              orderData={orderData}
              totals={{
                total: totalAmount,
                totalUSD: totalAmountUSD,
                currency
              }}
            />
          )}
          <hr className="separator" />
        </>
      )}
      <div className="tabTitle">
        <span className="titleText">Información de facturación del cliente</span>
        {documentCountry && (
          <div className="billingCountryText">
            <span>País: {documentCountry?.name || noDataText}</span>
          </div>
        )}
      </div>
      <div className="billingDataContainer">
        <div className="billingDataLineContainer" style={{ width: documentCountryIsBrasil ? '800px' : '' }}>
          <NudosCountrySelect
            label="País del Doc."
            componentSize="extraSmall"
            isDisabled
            defaultCountry={documentCountry}
          />
          <NudosTextInput
            label="Documento de identificación fiscal*"
            componentSize="small"
            placeholder={document || noDataText}
            defaultValue={document || noDataText}
            isFilled={false}
            isDeactivated
          />
          <NudosTextInput
            label="Razón social o denominación*"
            componentSize="small"
            placeholder={businessName || noDataText}
            defaultValue={businessName || noDataText}
            isFilled={false}
            isDeactivated
          />
          {documentCountryIsBrasil && stateRegistration && (
            <NudosTextInput
              label="Inscripción estatal (si aplica)"
              componentSize="small"
              placeholder={stateRegistration?.value || noDataText}
              defaultValue={stateRegistration?.value || noDataText}
              isFilled={false}
              isDeactivated
            />
          )}

          <NudosTextInput
            label="Ciudad*"
            componentSize="small"
            placeholder={city || noDataText}
            defaultValue={city || noDataText}
            isFilled={false}
            isDeactivated
          />
          <NudosTextInput
            label="CP*"
            componentSize="extraSmall"
            placeholder={zipCode || noDataText}
            defaultValue={zipCode || noDataText}
            isFilled={false}
            isDeactivated
          />
          <NudosTextInput
            label="Estado/ Provincia / Departamento"
            componentSize="small"
            placeholder={state || noDataText}
            defaultValue={state || noDataText}
            isFilled={false}
            isDeactivated
          />
          {documentCountryIsBrasil && (
            <NudosTextInput
              label="Barrio o vecindario*"
              componentSize="small"
              placeholder={'Barrio o vecindario'}
              defaultValue={colony || noDataText}
              isFilled={false}
              isDeactivated
            />
          )}

          {(documentCountryIsUruguay || fantasyName) && (
            <NudosTextInput
              label="Nombre fantasía*"
              componentSize="small"
              placeholder={fantasyName || noDataText}
              defaultValue={fantasyName || noDataText}
              isFilled={false}
              isDeactivated
            />
          )}
          {!addressInThirdLine && (
            <NudosTextInput
              label="Dirección de facturación*"
              componentSize="medium"
              placeholder={address || noDataText}
              defaultValue={address || noDataText}
              isFilled={false}
              isDeactivated
            />
          )}
          {documentCountryIsMexico && (
            <>
              <NudosTextInput
                label="Regimen fiscal"
                componentSize="large"
                placeholder={taxSituation?.name || noDataText}
                defaultValue={taxSituation?.name || noDataText}
                isFilled={false}
                isDeactivated
              />
              <NudosTextInput
                label="Uso de la factura"
                componentSize="small"
                placeholder={taxUse?.name || noDataText}
                defaultValue={taxUse?.name || noDataText}
                isFilled={false}
                isDeactivated
              />
            </>
          )}
          {(documentCountryIsUruguay || phone) && (
            <NudosPhoneInput
              label="Número de teléfono"
              componentSize="medium"
              placeholder="Número de teléfono"
              countriesList={[]}
              defaultCountryData={phoneCountry}
              defaultPhoneNumber={phone || noDataText}
              isDisabled
            />
          )}
          {addressInThirdLine && (
            <NudosTextInput
              label="Dirección de facturación*"
              componentSize={documentCountryIsMexico ? 'large' : 'medium'}
              placeholder={address || noDataText}
              defaultValue={address || noDataText}
              isFilled={false}
              isDeactivated
            />
          )}
          <NudosTextInput
            label="Indicaciones"
            componentSize="medium"
            placeholder={additionalReferences || noDataText}
            defaultValue={additionalReferences || noDataText}
            isFilled={false}
            isDeactivated
          />

          {!documentCountryIsBrasil && (
            <NudosTextInput
              label="Correo de facturación*"
              componentSize="small"
              placeholder={email || noDataText}
              defaultValue={email || noDataText}
              isFilled={false}
              isDeactivated
            />
          )}
          {taxSituationDocUrl && !documentCountryIsBrasil && (
            <div className="fileContainer">
              <NudosFileInput
                uploadedFileHandler={() => null}
                buttonText={`${getTaxDocumentText(documentCountry?.name)}`}
                customWidth="180px"
                isDisabled={true}
              />
              <NudosLink inactiveLinkText="Ver documento" activeLinkText="Ver documento" linkUrl={taxSituationDocUrl} />
            </div>
          )}
          {documentCountryIsBrasil && (
            <NudosUploadDownloadButton
              customClassName="uploadDownloadButtonClass"
              currentFileUrl={taxSituationDocUrl || ''}
              buttonEmptyText="Cadastro"
              buttonFilledText="Cadastro"
              downloadButtonText="Ver documento"
              uploadedFileHandler={() => {}}
              customHeight="28px"
              customWidth="155px"
              isDisabled
              fakeLabel
              showSkeletonLoader={false}
            />
          )}
        </div>
        {!!specialBusinessNameTypeId && (
          <div className="specialBusinessContainer">
            <div className="specialBusinessOption" key={`special-option-${specialBusinessNameTypeId}`}>
              <BlackCheck checked={true} />
              {getSpecialBusinessData('description', specialBusinessNameTypeId)}
            </div>
          </div>
        )}
      </div>
      {showBillingFileComponent && (
        <BillingFileComponent logisticServiceData={logisticServiceData} updateWithoutLoader={updateWithoutLoader} />
      )}{' '}
      {includePurchaseOrderSection && (
        <PurchaseOrderSection
          solutionType={'logisticService'}
          solutionId={logisticServiceData?.id || undefined}
          currentPurchaseOrder={logisticServiceData?.purchaseOrder}
          reloadDataCallback={updateWithoutLoader}
          purchaseOrderDocumentUrlOrigin={logisticServiceData?.purchaseOrderUrl || ''}
        />
      )}
    </div>
  );
};

export default BillingDataTab;
